import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { getRun, getRuns } from "../../services/workflowTriggers";
import {
  RUN_STATUSES,
  formatDateTime,
  formatDuration,
  runStatusTone,
} from "../../utils/workflowTriggers";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  color: var(--main-font-color);`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  color: var(--main-font-color);`;

const selectStyles = `
  padding: 5px 8px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
`;

const Select = styled.select`
  ${selectStyles}
  cursor: pointer;

  /* The dark theme sets "select, select option { color: black }", which out-specifies a single
     class — without !important the dropdown is black-on-dark and unreadable. */
  color: var(--main-font-color) !important;
  background-color: var(--main-bg) !important;

  option {
    color: var(--main-font-color) !important;
    background-color: var(--main-bg) !important;
  }
`;

const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 12px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--content-item-border-color);
  border-radius: 8px;
  overflow: hidden;

  color: var(--main-font-color);`;

const Row = styled.button`
  display: grid;
  grid-template-columns: 110px 1.4fr 1.2fr 90px 1fr;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 9px 12px;
  border: none;
  border-bottom: 1px solid var(--content-item-border-color);
  background: ${(props) => (props.$active ? "var(--sidebar-active-item-bg)" : "transparent")};
  color: var(--main-font-color);
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--sidebar-active-item-bg);
  }
`;

const HeadRow = styled(Row)`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #a0a6b1;
  cursor: default;
  pointer-events: none;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(props) => props.$tone};
  border: 1px solid ${(props) => props.$tone};
  background-color: ${(props) => `${props.$tone}1a`};
`;

const Muted = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #a0a6b1;
`;

const ErrorBanner = styled.div`
  padding: 8px 10px;
  font-size: 12.5px;
  border-radius: 6px;
  border: 1px solid #c0392b;
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2000000;
  width: min(560px, 92vw);
  display: flex;
  flex-direction: column;
  background-color: var(--dialog-bg);
  border-left: 1px solid var(--main-border-color);
  box-shadow: -8px 0 30px rgba(0, 0, 0, 0.3);

  color: var(--main-font-color);`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--main-border-color);

  color: var(--main-font-color);`;

const DrawerBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  color: var(--main-font-color);`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  color: var(--main-font-color);`;

const BlockTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #a0a6b1;
`;

const Code = styled.pre`
  margin: 0;
  padding: 9px 11px;
  font-size: 11.5px;
  line-height: 1.5;
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 8px;

  color: var(--main-font-color);`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 8px;
  font-size: 12.5px;

  span:first-child {
    color: #a0a6b1;
  }
`;

const isActiveStatus = (status) => ["queued", "running"].includes(String(status ?? "").toLowerCase());

const pretty = (value) => {
  if (value === undefined || value === null) return "—";
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

/**
 * Run history for a workflow, with filters, pagination and a per-run detail drawer.
 */
export const TriggerRunHistory = ({
  workflowPublicId,
  triggers = [],
  refreshToken = 0,
  triggerId = "",
  onTriggerIdChange = () => {},
}) => {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollTimer = useRef(null);

  const loadRuns = useCallback(async () => {
    if (!workflowPublicId) {
      setRuns([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    const result = await getRuns(workflowPublicId, { limit, offset, status, triggerId });
    setLoading(false);

    if (result?.success === false) {
      setRuns([]);
      setTotal(0);
      setError(result.message || "Could not load runs.");
      return;
    }

    setRuns(result.runs ?? []);
    setTotal(result.total ?? 0);
    setError(null);
  }, [workflowPublicId, limit, offset, status, triggerId]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns, refreshToken]);

  // Keep the list fresh while a run is in flight (poll every 4s).
  useEffect(() => {
    const hasActive = runs.some((run) => isActiveStatus(run.status));
    if (!hasActive) return undefined;

    const handle = window.setTimeout(() => loadRuns(), 4000);
    return () => window.clearTimeout(handle);
  }, [runs, loadRuns]);

  const openRun = async (run) => {
    if (!run?.public_id) return;

    setDetail({ run, loading: true });
    setDetailLoading(true);

    const result = await getRun(run.public_id);
    setDetailLoading(false);

    if (result?.success === false) {
      setDetail({ run, error: result.message || "Could not load the run." });
      return;
    }

    setDetail({ run: result.run ?? run, loading: false });
  };

  // Poll the open run's detail until it settles.
  useEffect(() => {
    if (!detail?.run || !isActiveStatus(detail.run.status)) return undefined;

    pollTimer.current = window.setTimeout(async () => {
      const result = await getRun(detail.run.public_id);
      if (result?.success !== false && result.run) setDetail({ run: result.run, loading: false });
    }, 4000);

    return () => {
      if (pollTimer.current) window.clearTimeout(pollTimer.current);
    };
  }, [detail]);

  const pageMax = Math.max(0, Math.ceil(total / limit) - 1);
  const pageIndex = Math.floor(offset / limit);

  return (
    <Wrapper>
      <Toolbar>
        <strong style={{ fontSize: "13px" }}>Runs</strong>
        <span style={{ flex: 1 }} />

        <Select value={status} onChange={(event) => { setStatus(event.target.value); setOffset(0); }}>
          <option value="">All statuses</option>
          {RUN_STATUSES.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </Select>

        <Select value={triggerId} onChange={(event) => { onTriggerIdChange(event.target.value); setOffset(0); }}>
          <option value="">All triggers</option>
          {triggers.map((trigger) => (
            <option key={trigger.public_id ?? trigger.name} value={trigger.public_id ?? ""}>
              {trigger.name || trigger.type}
            </option>
          ))}
        </Select>

        <Small type="button" onClick={() => loadRuns()} disabled={loading}>
          <Icon icon="lucide:refresh" /> Refresh
        </Small>
      </Toolbar>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}

      <Table>
        <HeadRow>
          <span>Status</span>
          <span>Trigger</span>
          <span>Started</span>
          <span>Duration</span>
          <span>Error</span>
        </HeadRow>

        {loading && runs.length === 0 ? (
          <Row as="div" style={{ cursor: "default" }}>
            <Muted>Loading runs…</Muted>
          </Row>
        ) : runs.length === 0 ? (
          <Row as="div" style={{ cursor: "default" }}>
            <Muted>No runs yet. Run it now or wait for a schedule.</Muted>
          </Row>
        ) : (
          runs.map((run) => {
            const trigger = triggers.find((entry) => entry.public_id === run.trigger_public_id);
            return (
              <Row
                key={run.public_id ?? run.id}
                type="button"
                $active={detail?.run?.public_id === run.public_id}
                onClick={() => openRun(run)}
              >
                <Chip $tone={runStatusTone(run.status)}>{run.status}</Chip>
                <span>{trigger?.name || run.trigger_type || "—"}</span>
                <span>{formatDateTime(run.started_at ?? run.created_at)}</span>
                <span>{formatDuration(run.duration_ms)}</span>
                <span style={{ color: run.error_present ? "#e74c3c" : "#a0a6b1" }}>
                  {run.error_present ? "error" : "—"}
                </span>
              </Row>
            );
          })
        )}
      </Table>

      <Toolbar>
        <Muted>
          {total} run{total === 1 ? "" : "s"}
        </Muted>
        <span style={{ flex: 1 }} />
        <Small type="button" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}>
          <Icon icon="lucide:chevron-left" /> Previous
        </Small>
        <Muted>
          Page {pageIndex + 1} of {pageMax + 1}
        </Muted>
        <Small
          type="button"
          onClick={() => setOffset(offset + limit)}
          disabled={offset + limit >= total}
        >
          Next <Icon icon="lucide:chevron-right" />
        </Small>
      </Toolbar>

      {detail ? (
        <Drawer>
          <DrawerHeader>
            <Chip $tone={runStatusTone(detail.run.status)}>{detail.run.status}</Chip>
            <strong style={{ fontSize: "13px" }}>Run {String(detail.run.public_id ?? "").slice(0, 8)}</strong>
            <span style={{ flex: 1 }} />
            <Small type="button" onClick={() => setDetail(null)}>
              <Icon icon="lucide:x" /> Close
            </Small>
          </DrawerHeader>

          <DrawerBody>
            {detail.error ? <ErrorBanner>{detail.error}</ErrorBanner> : null}
            {detailLoading && !detail.error ? <Muted>Loading run…</Muted> : null}

            <Block>
              <BlockTitle>Timings</BlockTitle>
              <DetailRow><span>Started</span><span>{formatDateTime(detail.run.started_at)}</span></DetailRow>
              <DetailRow><span>Finished</span><span>{formatDateTime(detail.run.finished_at)}</span></DetailRow>
              <DetailRow><span>Duration</span><span>{formatDuration(detail.run.duration_ms)}</span></DetailRow>
              <DetailRow><span>Trigger</span><span>{detail.run.trigger_type ?? "—"}</span></DetailRow>
              <DetailRow><span>Idempotency</span><span>{detail.run.idempotency_key ?? "—"}</span></DetailRow>
              {detail.run.poll_path ? <DetailRow><span>Poll path</span><span>{detail.run.poll_path}</span></DetailRow> : null}
            </Block>

            {detail.run.error ? (
              <Block>
                <BlockTitle>Error</BlockTitle>
                <Code>{typeof detail.run.error === "string" ? detail.run.error : pretty(detail.run.error)}</Code>
              </Block>
            ) : null}

            {detail.run.output && detail.run.output.success === false && detail.run.output.error ? (
              <Block>
                <BlockTitle>Node error</BlockTitle>
                <ErrorBanner>
                  {detail.run.output.error}
                  {detail.run.output.code ? ` (${detail.run.output.code})` : ""}
                </ErrorBanner>
              </Block>
            ) : null}

            <Block>
              <BlockTitle>Input</BlockTitle>
              <Code>{pretty(detail.run.input)}</Code>
            </Block>

            <Block>
              <BlockTitle>Context</BlockTitle>
              <Code>{pretty(detail.run.context)}</Code>
            </Block>

            <Block>
              <BlockTitle>Output</BlockTitle>
              <Code>{pretty(detail.run.output)}</Code>
            </Block>
          </DrawerBody>
        </Drawer>
      ) : null}
    </Wrapper>
  );
};

export default TriggerRunHistory;
